import React from 'react'

import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import AccountModule from 'modules/account'
import SettingsModule from 'modules/settings'

import { useFeatureManager, useFeatureManagerMetrics } from 'features/FeatureManager'

import CTA from '../Generic.CTA'
import Cart from '../Generic.Cart'
import Cancellation from '../Generic.Cancellation'

import styles from './IntegrationPurchaser.module.scss'


const IntegrationPurchaser = ({ pricingId, children = null }) => {
    const ctaId = 'generic_page_cta'
    const publisherId = useSelector(AccountModule.selectors.getPublisherId)
    const userId = useSelector(SettingsModule.selectors.getMeDataUserId)

    const featureManager = useFeatureManager({
        publisherId,
        userId,
    })

    const acceptedCtaSessionId = featureManager.acceptedCtaSessionId
    const appTier = featureManager.getAppTierByPricingId(pricingId)

    const hasAppTierAccess = appTier?.has_access
    const pricing = appTier?.getPricingById(pricingId)
    const hasSubscription = hasAppTierAccess && !!pricing?.subscription && pricing?.subscription?.status !== 'canceled'

    const [showCTA, setShowCTA] = useState(false)
    const [showCart, setShowCart] = useState(false)
    const [showCancellation, setShowCancellation] = useState(false)
    const [ctaSessionId, setCtaSessionId] = useState(null)

    const metrics = useFeatureManagerMetrics()

    useEffect(() => {
        if (hasSubscription) {
            setShowCTA(false)
            // setShowCancellation(true)
        } else {
            setShowCTA(true)
            setShowCancellation(false)
        }
    }, [hasSubscription])

    const dispatch = useDispatch()
    const ctaOnPresented = useCallback((eventData = {}) => {
        setCtaSessionId(eventData.cta_session_id)
        metrics.presentedCallToAction(eventData)
    }, [])

    const ctaOnDeclined = useCallback((eventData = {}) => {
        metrics.declinedCallToAction(eventData)
        setShowCTA(false)
    }, [dispatch, setShowCTA])

    const ctaOnAccepted = useCallback((eventData = {}) => {
        metrics.acceptedCallToAction(eventData)
        appTier.setAcceptedCtaSessionId(eventData.cta_session_id)

        setShowCart(true)
    }, [dispatch, setShowCart, appTier])

    const cartOnCartStarted = useCallback((eventData = {}) => {
        appTier.setCartSessionId(eventData.cart_session_id)
        metrics.startedCart(eventData)
    }, [dispatch, appTier])

    const cartOnCartCompleted = useCallback((eventData = {}) => {
        metrics.completedCart(eventData)
        appTier.setCartSessionId()
        appTier.setAcceptedCtaSessionId()

        setShowCart(false)
    }, [setShowCart, dispatch, appTier])

    const cartOnCartDismissed = useCallback((eventData = {}) => {
        metrics.dismissedCart(eventData)
        appTier.setCartSessionId()
        appTier.setAcceptedCtaSessionId()

        setShowCart(false)
    }, [setShowCart, dispatch, appTier])

    const cartOnCartFailed = useCallback((eventData = {}) => {
        appTier.setCartSessionId()
        appTier.setAcceptedCtaSessionId()

        metrics.failedCart(eventData)
    }, [dispatch, appTier])

    return (
        <div className={styles.purchaseAction}>
            {showCTA && (
                <CTA
                    id={ctaId}
                    app={appTier}
                    acceptedCtaSessionId={acceptedCtaSessionId}
                    onPresented={ctaOnPresented}
                    onDeclined={ctaOnDeclined}
                    onAccepted={ctaOnAccepted}
                >
                    {children}
                </CTA>
            )}
            {showCart && !hasSubscription && (
                <Cart
                    ctaId={ctaId}
                    app={appTier}
                    ctaSessionId={ctaSessionId}
                    onCartStarted={cartOnCartStarted}
                    onComplete={cartOnCartCompleted}
                    onCartDismissed={cartOnCartDismissed}
                    onCartFailed={cartOnCartFailed}
                    pricingId={pricingId}
                />
            )}
            {showCancellation && (
                <Cancellation
                    app={appTier}
                    pricingId={pricingId}
                    onCancellationStarted={cartOnCartStarted}
                    onCancellationCompleted={cartOnCartCompleted}
                    onCancellationFailed={cartOnCartFailed}
                    onCancellationAborted={cartOnCartStarted}
                />
            )}
        </div>
    )
}

export default IntegrationPurchaser
